import { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { getETDateString } from '@/lib/schedule';
import { isDefaultArt } from '@/lib/artwork';

/**
 * Loads ShowTrack rows for a show.
 * - When isLive is true: loads today's tracks (ET date), refreshing every 30s.
 * - When isLive is false: loads the most recent episode's tracks.
 *
 * Only confident=true rows are relevant to the UI; the caller filters.
 */
export function useShowTracks(showId, isLive) {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!showId) {
      setLoading(false);
      return;
    }
    let active = true;

    async function load() {
      let airDate;
      if (isLive) {
        airDate = getETDateString();
      } else {
        const recent = await base44.entities.ShowTrack
          .filter({ show_id: showId }, '-air_date', 1)
          .catch(() => []);
        airDate = recent?.[0]?.air_date;
      }
      if (!airDate) {
        if (active) { setTracks([]); setLoading(false); }
        return;
      }
      const res = await base44.entities.ShowTrack
        .filter({ show_id: showId, air_date: airDate }, '-played_at', 200)
        .catch(() => []);
      if (active) { setTracks(res || []); setLoading(false); }
    }

    setLoading(true);
    load();
    const id = setInterval(load, 30000);
    return () => { active = false; clearInterval(id); };
  }, [showId, isLive]);

  return { tracks, loading };
}

/**
 * Resolves cover art for a single track via the resolveTrackCover backend
 * function (which also saves the result back to the ShowTrack row).
 */
export function useTrackCover(track) {
  const [cover, setCover] = useState(track?.cover_url || null);
  const isDefault = isDefaultArt(track?.cover_url);

  useEffect(() => {
    setCover(track?.cover_url || null);
    // Real (non-default) cover — no lookup needed.
    if (!isDefault && track?.cover_url) return;
    // Default or empty — resolve via resolveTrackCover (saves back to the row).
    if (!track?.id || !track?.artist || !track?.title) return;

    let active = true;
    base44.functions
      .invoke('resolveTrackCover', {
        trackId: track.id,
        artist: track.artist,
        title: track.title,
      })
      .then((res) => {
        if (active) {
          // If a cover is found, use it. Otherwise keep the original (the
          // default placeholder / Yakaleo logo) so the tile never goes blank.
          setCover(res?.data?.cover_url || track?.cover_url || null);
        }
      })
      .catch(() => {});
    return () => { active = false; };
  }, [track?.id, track?.cover_url, track?.artist, track?.title, isDefault]);

  return cover;
}